import type { DashboardFilterOption } from '@/components/dashboard/dashboard-filter';
import type { UserApi } from '@/types/api/users';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

/** statusId representing an active user in the backend */
const ACTIVE_STATUS_ID = 1;

/** Deterministic avatar colour based on user id */
const AVATAR_COLORS = [
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#F59E0B', // Orange
  '#10B981', // Green
  '#6366F1', // Indigo
  '#EF4444', // Red
  '#14B8A6', // Teal
];

function getAvatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getInitials(firstName: string | null, lastName: string | null, email: string): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.trim();
  if (initials) return initials.toUpperCase();
  return email.charAt(0).toUpperCase();
}

/** Demo-mode hardcoded users (matches the original static list) */
const demoFilterUsers: DashboardFilterOption[] = [
  { id: 'user-001', name: 'Sarah Johnson', initials: 'SJ', type: 'user', avatarColor: '#3B82F6' },
  { id: 'user-002', name: 'Mike Chen', initials: 'MC', type: 'user', avatarColor: '#8B5CF6' },
  { id: 'user-003', name: 'Emily Rodriguez', initials: 'ER', type: 'user', avatarColor: '#EC4899' },
  { id: 'user-004', name: 'David Kim', initials: 'DK', type: 'user', avatarColor: '#F59E0B' },
];

/**
 * Hook to fetch users for the Dashboard Manager View filter dropdown.
 *
 * - In **live** mode it calls `GET /api/v1/users/company`, filters to active users,
 *   sorts by lastName then firstName, and maps them to DashboardFilterOption[].
 * - In **demo** mode it returns the original hardcoded list.
 */
export function useDashboardFilterUsers() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DashboardFilterOption[], Error>(
    ['dashboard-filter-users'],
    async ({ signal }) => {
      const response = await apiGet<UserApi[]>('/api/v1/users/company', undefined, signal);

      return response
        .filter((u) => u.statusId === ACTIVE_STATUS_ID)
        .sort((a, b) => {
          const lastCmp = (a.lastName ?? '').localeCompare(b.lastName ?? '');
          if (lastCmp !== 0) return lastCmp;
          return (a.firstName ?? '').localeCompare(b.firstName ?? '');
        })
        .map((u) => {
          const firstName = (u.firstName ?? '').trim();
          const lastName = (u.lastName ?? '').trim();
          const fullName = [firstName, lastName].filter(Boolean).join(' ');
          return {
            id: String(u.id),
            name: fullName || u.email,
            initials: getInitials(u.firstName, u.lastName, u.email),
            type: 'user' as const,
            avatarColor: getAvatarColor(u.id),
          };
        });
    },
    demoFilterUsers,
    {
      staleTime: 5 * 60 * 1000, // cache for 5 minutes
    },
  );
}
